import { pluralize } from "@inrev/common";
import {
	type DataNeeded,
	type ProCon,
	type ProConRelationship,
	relatedProConId,
	relationshipVerb,
} from "@portal/api/decisioning";
import { cn } from "../../../../lib/utils";
import { ClickableIcon, Tag, Text } from "../../../../primitives";
import {
	DATA_NEEDED_IMPORTANCE_LABELS,
	DATA_NEEDED_IMPORTANCE_TAG_COLORS,
} from "../../decisioningLabels";
import { useDecisioningData } from "../DecisioningDataContext";
import { useFactorsBoard } from "./FactorsBoardContext";
import { formatObservedValue } from "./observedValue";
import { relationshipTone } from "./RelationLink";

export type FactorCardDetailsProps = {
	proCon: ProCon;
	relationships: readonly ProConRelationship[];
	openRequests: readonly DataNeeded[];
	testId: string;
};

/** What an expanded card adds: the full rationale, each relationship, and linked open requests. */
export const FactorCardDetails = ({
	proCon,
	relationships,
	openRequests,
	testId,
}: FactorCardDetailsProps) => {
	const { proConsById } = useDecisioningData();
	const { pendingIds, removeRelationship } = useFactorsBoard();

	return (
		<div className="flex flex-col gap-2" data-testid={`${testId}-details`}>
			{proCon.detail && (
				<Text
					color="secondary"
					className="block whitespace-pre-wrap break-words text-[13px] font-normal leading-[18px] [text-box-trim:none]"
					data-testid={`${testId}-rationale`}
				>
					{proCon.detail}
				</Text>
			)}
			{proCon.observedValue !== null && (
				<Text
					type="caption"
					color="secondary"
					className="block font-normal [text-box-trim:none]"
					data-testid={`${testId}-value`}
				>
					<span className="font-semibold">
						{formatObservedValue(proCon.observedValue, proCon.factor.valueType)}
					</span>
					{proCon.observedValueSource === "underwriter"
						? " · entered by underwriter"
						: " · from account data"}
				</Text>
			)}
			<Text type="caption" color="tertiary" className="block font-normal [text-box-trim:none]">
				{proCon.factor.group.lens} · {proCon.factor.group.name}
			</Text>
			{relationships.map((relationship) => {
				const other = proConsById.get(relatedProConId(relationship, proCon.id));
				if (!other) return null;
				const tone = relationshipTone(relationship.type, proCon.kind);
				return (
					<div
						key={relationship.id}
						data-testid={`${testId}-relationship-${relationship.id}`}
						className={cn("flex items-start gap-2 rounded-md px-2.5 py-2", tone.background)}
					>
						<div className="flex min-w-0 flex-1 flex-col gap-1">
							<Text
								type="caption"
								className={cn("block font-semibold [text-box-trim:none]", tone.text)}
								data-testid={`${testId}-relationship-${relationship.id}-sentence`}
							>
								{`${proCon.factor.name} ${relationshipVerb(relationship, proCon.id)} ${other.factor.name}.`}
							</Text>
							{relationship.rationale && (
								<Text
									type="caption"
									color="secondary"
									className="block font-normal [text-box-trim:none]"
								>
									{relationship.rationale}
								</Text>
							)}
						</div>
						<ClickableIcon
							type="x-mark"
							size="micro"
							color="tertiary"
							aria-label={`Remove connection to ${other.factor.name}`}
							disabled={pendingIds.has(relationship.id) || pendingIds.has(proCon.id)}
							testId={`${testId}-relationship-${relationship.id}-remove`}
							onClick={() => removeRelationship(relationship.id)}
						/>
					</div>
				);
			})}
			{openRequests.length > 0 && (
				<ul
					className="m-0 flex list-none flex-col gap-1 p-0"
					aria-label={pluralize(openRequests.length, "open request", "open requests")}
				>
					{openRequests.map((request) => (
						<li
							key={request.id}
							data-testid={`${testId}-request-${request.id}`}
							className="flex list-none items-center justify-between gap-2 rounded-md bg-surface-base px-2.5 py-1.5"
						>
							<Text
								type="caption"
								color="secondary"
								className="min-w-0 flex-1 font-normal"
								truncate
							>
								{request.title}
							</Text>
							<Tag color={DATA_NEEDED_IMPORTANCE_TAG_COLORS[request.importance]}>
								{DATA_NEEDED_IMPORTANCE_LABELS[request.importance]}
							</Tag>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};
